import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material';
import useTrackingCookies from 'parent-portal/shared/EngagementSourceTracking';
import usePublicSessionTracking from 'parent-portal/shared/PublicSessionTracking';
import { client, consoleLogInDev } from 'shared';
import { Colors } from 'library';
import { Card, Chip, Divider, Grid, Link, MultiSelect, PillButton, Row, Select, Stack, Text, Title } from 'library/ui';
import useQueryParams from 'shared/QueryParamsHook';
import { Environment } from 'environmentVariables';
import { getStateAbbr, formatStateName, parseJobTypeLabels } from './jobUtils';
import { useHistory } from 'react-router';
import { HeroBand, HeroTitle } from './components';

interface IIndeedJob {
    id: number;
    city: string;
    state: string;
    zip_code: string;
    title: string;
    alt_title: string;
    pay_string: string;
    job_type: string;
    description_preview: string;
}

interface IFilters {
    states: string[];
    cities_by_state: Record<string, string[]>;
}

function navigateWithState(state: string) {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    const qs = params.toString();
    window.location.href = qs ? `listings?${qs}` : 'listings';
}

function buildCityParams(state: string, cities: string[]): string {
    const params = new URLSearchParams();
    if (state) params.set('state', state);
    cities.forEach((c) => params.append('city', c));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export default function Workers() {
    useTrackingCookies('/workers');
    const { usePageLoadTracking } = usePublicSessionTracking('workers');
    usePageLoadTracking();
    const query = useQueryParams();
    const [jobs, setJobs] = useState<IIndeedJob[]>([]);
    const [filters, setFilters] = useState<IFilters>({ states: [], cities_by_state: {} });
    const selectedState = query.get('state') || '';
    const initialCities = query.getAll('city');
    const [pendingCities, setPendingCities] = useState<string[]>(initialCities);

    useEffect(() => {
        client('api/indeed-jobs/list-jobs/filters/')
            .then((response: IFilters) => setFilters(response))
            .catch((error: unknown) => consoleLogInDev(error));
    }, []);

    useEffect(() => {
        const qs = buildCityParams(selectedState, initialCities);
        client(`api/indeed-jobs/list-jobs/${qs}`)
            .then((response: IIndeedJob[]) => setJobs(response))
            .catch((error: unknown) => consoleLogInDev(error));
    }, [selectedState, initialCities.join(',')]);

    const availableCities = useMemo(() => {
        if (!selectedState) {
            const allCities = Object.values(filters.cities_by_state).flat();
            return Array.from(new Set(allCities)).sort();
        }
        return filters.cities_by_state[selectedState] || [];
    }, [selectedState, filters]);

    function handleCitiesClose() {
        if (pendingCities.join(',') !== initialCities.join(',')) {
            const qs = buildCityParams(selectedState, pendingCities);
            window.location.href = qs ? `listings${qs}` : 'listings';
        }
    }

    const hasFilters = selectedState || initialCities.length > 0;

    return (
        <Stack fullWidth>
            <HeroBand justify="center" align="center">
                <Stack maxWidth="lg" fullWidth gap="sm">
                    <HeroTitle size="h1" tone="overlay">
                        Join Our Team
                    </HeroTitle>
                    <Stack maxWidth="md">
                        <Text tone="overlay">
                            Find childcare teaching positions near you with competitive pay, flexible scheduling, and
                            daily pay options.
                        </Text>
                    </Stack>
                </Stack>
            </HeroBand>

            <Stack align="center" fullWidth padding="md">
                <Stack maxWidth="lg" fullWidth gap="lg">
                    <Row gap="sm" wrap align="center">
                        <SelectWrap>
                            <Select
                                value={selectedState}
                                onChange={(value) => navigateWithState(value)}
                                tone="primary"
                                placeholder="All States"
                                fullWidth
                                options={[
                                    { key: '', value: 'All States' },
                                    ...filters.states.map((state) => ({ key: state, value: formatStateName(state) })),
                                ]}
                            />
                        </SelectWrap>
                        <SelectWrap>
                            <MultiSelect
                                value={pendingCities}
                                onChange={setPendingCities}
                                onClose={handleCitiesClose}
                                tone="primary"
                                fullWidth
                                placeholder="All Cities"
                                options={availableCities.map((city) => ({ key: city, value: city }))}
                                renderSummary={(labels) =>
                                    labels.length === 1 ? labels[0] : `${labels.length} cities selected`
                                }
                            />
                        </SelectWrap>
                        {hasFilters && (
                            <Link to="/listings" color={Colors.textSecondary} size="sm">
                                Clear filters
                            </Link>
                        )}
                    </Row>

                    <Text size="sm" tone="secondary">
                        {jobs.length} {jobs.length === 1 ? 'position' : 'positions'} available
                        {selectedState ? ` in ${formatStateName(selectedState)}` : ''}
                        {initialCities.length > 0 ? `, ${initialCities.join(', ')}` : ''}
                    </Text>

                    <Grid minColumnWidth={300} gap="lg">
                        {jobs.map((x) => (
                            <JobListingCard key={x.id} job={x} />
                        ))}
                    </Grid>
                    {!jobs.length && (
                        <Stack align="center" gap="sm">
                            <Title size="h2">No positions found</Title>
                            <Text tone="secondary" align="center">
                                Try adjusting your filters or check back soon for new openings.
                            </Text>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
}

function JobListingCard({ job }: { job: IIndeedJob }) {
    const history = useHistory();
    const typeLabels = parseJobTypeLabels(job.job_type);
    const detailHref =
        Environment === 'development'
            ? `http://localhost:8000/sitter/indeed-job/${job.id}/`
            : `/job-listings/${job.id}`;

    function handleApplyNow() {
        history.push(`/application/apply/${job.id}`);
    }

    function stopPropagation(e: React.MouseEvent) {
        e.preventDefault();
        e.stopPropagation();
    }

    return (
        <JobCard padding="sm" href={detailHref} fullHeight>
            <Stack gap="md" fullHeight justify="between">
                <Stack gap="sm">
                    <Text size="xs" weight="bold" tone="accent" uppercase>
                        {job.city}, {getStateAbbr(job.state)}
                    </Text>
                    <JobTitle size="lg" weight="bold">
                        {job.alt_title}
                    </JobTitle>
                    <Row gap="sm">
                        {typeLabels.map((label) => {
                            const isFullTime = label.toLowerCase().includes('full');
                            return (
                                <Chip
                                    key={label}
                                    variant="filled"
                                    bgColor={isFullTime ? Colors.secondarySoft : Colors.accentSoft}
                                    textColor={isFullTime ? Colors.secondary : Colors.accent}
                                >
                                    {label}
                                </Chip>
                            );
                        })}
                    </Row>
                    {job.description_preview && (
                        <Text size="sm" tone="secondary" lineClamp={3}>
                            {job.description_preview}...
                        </Text>
                    )}
                </Stack>
                <Stack gap="md">
                    <Divider />
                    <Row justify="between" align="center">
                        <Text weight="bold">{job.pay_string}</Text>
                        <span onClick={stopPropagation}>
                            <PillButton onClick={handleApplyNow} color={Colors.secondary}>
                                Apply Now
                            </PillButton>
                        </span>
                    </Row>
                </Stack>
            </Stack>
        </JobCard>
    );
}

const SelectWrap = styled('div')({
    minWidth: 180,
});

const JobCard = styled(Card)({
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    '&:hover': {
        boxShadow: '0 8px 24px rgba(0,47,77,0.12)',
        transform: 'translateY(-2px)',
    },
});

const JobTitle = styled(Text)({
    '&:hover': {
        color: Colors.secondary,
        textDecoration: 'underline',
    },
});
